<script>
import { resolveUrl } from '@shared/services/IframeEmbedUtils';
import responsiveMixin from '@shared/mixins/responsive';
import VideoCtas from '@shared/components/VideoCtas.vue';

const controls = [
  'play-large',
  'play',
  'progress',
  'current-time',
  'mute',
  'volume',
  'captions',
  'settings',
  'pip',
  'airplay',
];

export default {
  mixins: [
    responsiveMixin({ isTouch: 800 }),
  ],
  components: {
    VideoCtas,
  },
  props: {
    parser: {
      type: String,
      default: null,
    },
    attrs: {
      type: [Object, String],
      default: null,
    },
    url: {
      type: String,
      required: true,
    },
    extraData: {
      type: [Object, Array],
      default: null,
    },
  },
  data() {
    return {
      isLoading: false,
      iattrs: this.attrs,
      src: false,
      chatSrc: false,
      hasPlayerMounted: false,
    };
  },
  updated() {
    if (!this.$refs.player && this.hasPlayerMounted) {
      this.hasPlayerMounted = false;
    }

    if (this.$refs.player && !this.hasPlayerMounted) {
      this.hasPlayerMounted = true;
      this.preventYouTubeAutoCaptions(this.$refs.player.player);
    }
  },
  beforeDestroy() {
    if (this.$refs.player?.player) {
      this.$refs.player.player.destroy();
    }
  },
  watch: {
    url: {
      immediate: true,
      handler: 'getSRC',
    },
  },
  computed: {
    host() {
      return window.location.hostname;
    },
    isVideoPlayer() {
      return ['vimeo', 'youtube', 'youtube_live', 'plyr']
        .indexOf(this.parser) > -1;
    },
    hasTimecode() {
      return (
        this.extraData
        && !this.extraData.nativePlayer
        && this.extraData.timecodes
        && this.extraData.timecodes.length
      );
    },
    hasCtas() {
      return (
        this.extraData
        && this.extraData.ctas
        && this.extraData.ctas.length
      );
    },
    vimeoPrivateHash() {
      const { src } = this;
      const hashMatch = src && src.match(/\?h=([A-z0-9]+)/);

      return hashMatch && hashMatch[1];
    },
    hasChatEnabled() {
      return !!(this.extraData && this.extraData.hasChatEnabled);
    },
    controls() {
      return this.isTouch ? [
        ...controls,
        'fullscreen',
      ] : [
        ...controls,
        'rewind',
        'fast-forward',
        'fullscreen',
      ];
    },
  },
  methods: {
    getSRC(url) {
      this.isLoading = true;

      const promises = [
        resolveUrl(url, this.attrs, this.parser),
      ];

      if (this.hasChatEnabled) {
        promises.push(resolveUrl(url, {}, 'youtube_live_chat'));
      }

      Promise.all(promises)
        .then(([parserResult, chatResult]) => {
          if (parserResult) {
            this.iattrs = parserResult.iattrs;
            this.src = parserResult.src;

            if (chatResult) {
              this.chatSrc = chatResult.src;
            }
          } else {
            this.src = false;
            this.chatSrc = false;
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    goToTimecode(timecode) {
      const { player } = this.$refs.player;
      const timeSplitted = timecode.time.split(':');
      const timeInSeconds = Math.min(
        player.duration - 1,
        parseInt(timeSplitted[0] * 60, 10) + parseInt(timeSplitted[1], 10),
      );

      if (!Number.isNaN(timeInSeconds)) {
        player.play();
        player.currentTime = timeInSeconds;
      }
    },
    playerPrevSeconds() {
      this.$refs.player.player.currentTime -= 10;
    },
    playerNextSeconds() {
      this.$refs.player.player.currentTime += 10;
    },
    preventYouTubeAutoCaptions(player) {
      let applied = false;

      // YouTube turns captions on by itself and ignores cc_load_policy=0;
      // clearing the track then reloading makes YouTube remember "off" for this site.
      const disableCaptions = () => {
        const youtube = player.embed;

        if (applied || player.provider !== 'youtube' || !youtube?.getOption) {
          return;
        }

        let track = null;

        try {
          track = youtube.getOption('captions', 'track');
        } catch (e) {
          return;
        }

        if (!track?.languageCode) {
          return;
        }

        applied = true;
        youtube.setOption('captions', 'track', {});
        youtube.loadVideoById({
          videoId: youtube.getVideoData().video_id,
          startSeconds: youtube.getCurrentTime(),
        });
      };

      const listenToYouTube = () => {
        player.embed?.addEventListener?.('onApiChange', disableCaptions);
      };

      if (player.ready) {
        listenToYouTube();
      } else {
        player.on('ready', listenToYouTube);
      }

      player.on('playing', disableCaptions);
      player.on('timeupdate', disableCaptions);
    },
  },
  i18n: {
    messages: {
      en: {
        video_chapters: 'Video chapters',
        loading_error: 'There was a loading problem...',
      },
      fr: {
        video_chapters: 'Chapitrage de la vidéo',
        loading_error: 'Il y a eu un problème de chargement...',
      },
    },
  },
};
</script>

<template>
  <div class="vsecomp">
    <b-skeleton v-if="isLoading" height="360" />
    <template v-else-if="src !== false">
      <div class="vsecomp_content">
        <div class="vsecomp_embed">
          <template v-if="!isVideoPlayer">
            <iframe
              class="has-background-black"
              :src="src"
              v-bind="iattrs"
            />
            <slot name="caption" />
          </template>
          <template v-else>
            <div class="vsecomp_embed_wrap" :class="{ hasChatEnabled }">
              <div class="vsecomp_embed_video">
                <vue-plyr
                  v-if="!extraData || !extraData.nativePlayer"
                  ref="player"
                  :options="{
                    vimeo: { h: vimeoPrivateHash },
                    controls,
                  }"
                >
                  <div class="plyr__video-embed">
                    <iframe :src="src" v-bind="iattrs" />
                    <div
                      v-show="isTouch"
                      style="width:27%;height:100%;position:absolute;z-index:2;top:0;left:0;background:transparent;"
                      @dbltap.stop.prevent="playerPrevSeconds"
                    />
                    <div
                      v-show="isTouch"
                      style="width:27%;height:100%;position:absolute;z-index:2;top:0;right:0;background:transparent;"
                      @dbltap.stop.prevent="playerNextSeconds"
                    />

                    <VideoCtas
                      v-if="hasCtas"
                      :ctas="extraData.ctas"
                      :player="hasPlayerMounted && $refs.player ? $refs.player.player : null"
                    />
                  </div>
                </vue-plyr>
                <iframe
                  v-else
                  class="has-background-black"
                  :src="src"
                  v-bind="iattrs"
                />
              </div>
              <div v-if="hasChatEnabled" class="vsecomp_embed_sidebar bradius-4">
                <iframe class="w-full h-full" :src="chatSrc" />
              </div>
            </div>
            <slot name="caption" />
            <div v-if="hasTimecode" class="box content has-text-justified mt-3">
              <h3 class="is-size-5-touch">
                {{ $t('video_chapters') }}
              </h3>
              <ul>
                <li v-for="(timecode, k) in extraData.timecodes" :key="k">
                  {{ timecode.title }} :
                  <a href="#" @click.prevent="goToTimecode(timecode)">
                    {{ timecode.time }}
                  </a>
                </li>
              </ul>
            </div>
          </template>
        </div>
      </div>
    </template>
    <template v-else>
      <slot v-if="$slots.default" />
      <p v-else class="notification is-danger">
        {{ $t('loading_error') }}
      </p>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.vsecomp {
  &_embed {
    &_wrap {
      display: flex;

      &.hasChatEnabled {
        min-height: 496px;
      }
    }

    &_video {
      width: 100%;
    }

    &_sidebar {
      flex: none;
      width: 35%;
      min-width: 320px;
      margin-left: pxToRem(12);
      border: 2px solid $theme_color_black_10;
    }
  }

  @include mobile {
    &_embed {
      &_wrap {
        display: block;

        &.hasChatEnabled {
          min-height: auto;
        }
      }

      &_sidebar {
        width: 100%;
        height: 496px;
        margin-left: 0;
      }
    }
  }
}
</style>
